import { Fragment, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react'
import { getInventory, getProduct, trackEvent } from '../../lib/api'
import Cart from '../../components/Cart'
import { CartContext } from '../../lib/contexts/cart'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'
import { NextSeo } from 'next-seo'
import SimilarProducts from '../../components/SimilarProducts'
import { faShoppingBasket } from '@fortawesome/free-solid-svg-icons'
import { getShopConfiguration } from '../../lib/utils'

const varietyInit = {}

function varietyReducer(state, action) {
  switch (action.type) {
    case 'reset_with_selection':
      return { ...action.feature }

    case 'select':
      return { ...state, ...action.feature }

    default:
      return {}
  }
}

const featureTitles = {
  color: 'Colors',
  flavor: 'Flavors',
  grade: 'Grades',
  size: 'Sizes',
}

const featureWord = {
  color: 'color',
  flavor: 'flavor',
  grade: 'grade',
  size: 'size',
}

/**
 * Returns features of the product in the form[i]
 * ```[
 *  {color: 'red', size: 12 },
 *  {color: 'white', size: 10}
 * ]```
 * @param {object} product
 * @returns {array}
 */
function getVariants(product) {
  return product.variants.map((v) => {
    return v.features
  })
}

function getStockStyle(count) {
  if (count <= 3) {
    return 'alert alert-danger py-1 px-2 d-inline'
  }

  return 'alert alert-secondary py-1 px-2 d-inline'
}

export default function Detail({ product }) {
  const [selectedImage, setSelectedImage] = useState(0)
  const [features, setFeatures] = useState()
  const cart = useContext(CartContext)

  const [selectedVariant, setSelectedVariant] = useState()

  // classname mismatch between server and client. This makes sure the add/remove to/from cart
  // only renders on client
  const [showAddButton, setShowAddButton] = useState(false)

  const [variety, selectVariety] = useReducer(varietyReducer, varietyInit)

  const [error, setError] = useState()

  const inventory = useRef()

  const [inventoryCount, setInventoryCount] = useState(-1)

  /**
   * Returns the feature (of variant) not selected
   * @param {object} variety
   */
  const getMissing = useCallback(
    (variety) => {
      if (product.variants.length < 1) return

      const varietyKeys = Object.keys(variety)
      const featureKeys = Object.keys(features)

      const featureSelects = varietyKeys.length
      const featuresCount = featureKeys.length

      if (featuresCount !== featureSelects) {
        for (let i = 0; i < featuresCount; i++) {
          if (varietyKeys.indexOf(featureKeys[i]) === -1) {
            return featureKeys[i]
          }
        }
      }
    },
    [features, product.variants.length]
  )

  const handleSelection = useCallback(() => {
    if (!features) {
      return
    }

    if (getMissing(variety)) {
      setSelectedVariant(undefined)
      return
    }

    const varietyKeys = Object.keys(variety)

    const { _id } = product.variants.filter((v) => {
      return varietyKeys
        .map((vi) => {
          return variety[vi] === v.features[vi]
        })
        .reduce((x, y) => x && y)
    })[0]

    setSelectedVariant(_id)
  }, [features, getMissing, product.variants, variety])

  function selectVariant(feature, value, disabled) {
    if (disabled) {
      selectVariety({
        feature: { [feature]: value },
        type: 'reset_with_selection',
      })
    } else {
      selectVariety({
        feature: { [feature]: value },
        type: 'select',
      })
    }
  }

  function isGhost(feature) {
    const selectedFeatures = Object.keys(variety)

    // no selection made at all
    if (selectedFeatures.length === 0) {
      return () => false
    }

    // filter out features where the selection is made
    const variants = getVariants(product)
    const valids = variants.filter((v) => {
      // but last, so that making full selection will not disable other last options
      const butLast = selectedFeatures.slice(0, Object.keys(features).length - 1)

      if (butLast.length < 1) return false

      return butLast.map((si) => v[si] === variety[si]).reduce((x, y) => x && y)
    })

    return (value) =>
      valids
        .map((v) => {
          return v[feature]
        })
        .indexOf(value) === -1
  }

  function addToCart() {
    setError('')

    const missing = getMissing(variety)
    if (missing) {
      setError(`You need to select a ${featureWord[missing]}`)
      return
    }

    if (product.limitedStock && getInventoryCount() === 0) {
      return
    }

    cart.addItem(product, selectedVariant)
  }

  function removeFromCart() {
    cart.removeItem(product, selectedVariant)
  }

  // analytics
  const viewProduct = useCallback(() => {
    trackEvent({
      action: 'product_view',
      category: 'shop',
      extras: {
        product: product._id,
        shop: product.shop,
      },
    })
  }, [product])

  const getInventoryCount = useCallback(() => {
    if (product.limitedStock && (inventory.current || []).length > 0) {
      const stock = inventory.current.find(
        (inv) => inv.product === product._id && selectedVariant === inv.variant
      )

      return stock?.stock
    }

    return -1
  }, [product._id, product.limitedStock, selectedVariant])

  const setUpInventory = useCallback(async () => {
    inventory.current = await getInventory({ product: product._id })
    setInventoryCount(getInventoryCount())

    setShowAddButton(getInventoryCount() !== 0)
  }, [getInventoryCount, product._id])

  useEffect(() => {
    const variants = getVariants(product)

    if (variants.length > 0) {
      const commonFeatures = Object.keys(variants[0]) // common features
      const d = {}

      commonFeatures.forEach((fi) => {
        d[fi] = Array.from(
          new Set(
            variants.map((v) => {
              return v[fi]
            })
          )
        )
      })

      setFeatures(d)
    }

    viewProduct()
    setUpInventory()
    setShowAddButton(true)
  }, [product, selectedVariant, setUpInventory, viewProduct])

  useEffect(() => {
    handleSelection()
  }, [handleSelection])

  return (
    <>
      <NextSeo
        title={product.title}
        description={product.summary.substring(0, 80)}
        openGraph={{
          images: product.images.map((img) => {
            return {
              url: img.url,
            }
          }),
        }}
      />
      <section className="container py-3">
        <div className="d-block d-lg-none">
          <p className="h3 mb-0">{product.title}</p>
          <p className="text-muted">
            <Link href={`/products/?category=${product.category._id}`}>
              <a>{product.category.title}</a>
            </Link>
          </p>

          {Boolean(product.sellAt) && product.sellAt > 0 ? (
            <p>
              <span className="h5">
                {product.price.currency} {product.sellAt}{' '}
              </span>
              <span className="text-muted" style={{ textDecoration: 'line-through' }}>
                {product.price.currency} {product.price.value}
              </span>
            </p>
          ) : (
            <p>
              <span className="h5">
                {product.price.currency} {product.price.value}
              </span>
            </p>
          )}
        </div>

        <div className="row">
          <div className="col-lg-5 col-12">
            <div className="mb-3">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={product.images[selectedImage].url} alt="" className="img-fluid" />
            </div>

            {product.images.length > 1 && (
              <div className="row mb-4">
                {product.images.map((image, i) => {
                  return (
                    <div className="col-3" key={i}>
                      <div
                        className={
                          'ratio ratio-square rounded bg-cover cursor-pointer ' +
                          (selectedImage === i ? 'border border-4 border-primary' : '')
                        }
                        style={{ backgroundImage: `url(${image.url})` }}
                        onClick={() => setSelectedImage(i)}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="col-lg-6 col-12">
            <div className="d-lg-block d-none">
              <h1 className="h3 mb-0">{product.title}</h1>
              <p className="text-muted">
                <Link href={`/products/?category=${product.category._id}`}>
                  <a>{product.category.title}</a>
                </Link>
              </p>

              {Boolean(product.sellAt) && product.sellAt > 0 ? (
                <p>
                  <span className="h5">
                    {product.price.currency} {product.sellAt}{' '}
                  </span>
                  <span className="text-muted" style={{ textDecoration: 'line-through' }}>
                    {product.price.currency} {product.price.value}
                  </span>
                </p>
              ) : (
                <p>
                  <span className="h5">
                    {product.price.currency} {product.price.value}
                  </span>
                </p>
              )}
            </div>

            <p className="whitespace-pre">{product.summary}</p>

            {Boolean(features) &&
              Object.keys(features).map((f) => {
                const isDisabled = isGhost(f)
                return (
                  <Fragment key={f}>
                    <p className="text-muted mt-4">{featureTitles[f]}</p>

                    <p className="mb-0">
                      {features[f].map((value, index) => {
                        let classExt = ''

                        const disabled = isDisabled(value)
                        if (disabled) {
                          classExt = 'boder-light text-muted'
                        }

                        const selected = value === variety[f]
                        if (selected) {
                          classExt = 'border-secondary text-secondary'
                        }

                        return (
                          <span
                            className={
                              'p-1 rounded border d-inline-block mb-2 me-3 hover-light cursor-pointer user-select-none ' +
                              classExt
                            }
                            onClick={() => selectVariant(f, value, disabled)}
                            key={index}
                          >
                            {value}
                          </span>
                        )
                      })}
                    </p>
                  </Fragment>
                )
              })}

            <div className="mt-5">
              {Boolean(error) && (
                <div className="d-block mb-3">
                  <p className="alert py-1 px-2 alert-danger d-inline">{error}</p>
                </div>
              )}

              {inventoryCount === 0 ? (
                <div className="d-block">
                  <p className={getStockStyle(inventoryCount)}>This item is out of stock</p>
                </div>
              ) : (
                inventoryCount > -1 && (
                  <div className="d-block">
                    <p className={getStockStyle(inventoryCount)}>
                      There are {inventoryCount} of this item in stock
                    </p>
                  </div>
                )
              )}

              <div className="mt-3">
                {showAddButton && (
                  <p>
                    {cart.contains(product, selectedVariant) ? (
                      <button className="btn btn-outline-primary" onClick={removeFromCart}>
                        Remove from cart
                      </button>
                    ) : (
                      <button className="btn btn-primary" onClick={addToCart}>
                        Add to cart
                      </button>
                    )}
                  </p>
                )}
              </div>

              <>
                {showAddButton && (
                  <div>
                    {cart.contains(product, selectedVariant) && (
                      <div className="mt-3">
                        <Link href="/checkout">
                          <a className="btn btn-secondary text-white">
                            <FontAwesomeIcon icon={faShoppingBasket} /> Proceed to checkout
                          </a>
                        </Link>

                        <p className="text-muted">
                          You can add more items to your checkout from the{' '}
                          <Link href="/products">
                            <a className="underline text-secondary">products page</a>
                          </Link>
                          .
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            </div>
          </div>
        </div>
      </section>

      <section className="container">
        <SimilarProducts product={product} />
      </section>
      <Cart />
    </>
  )
}

export async function getServerSideProps({ query, req }) {
  const { slug } = query
  const product = await getProduct(slug)

  const config = await getShopConfiguration(req.locals.config.shop)

  return {
    props: {
      product,
      ...config.props,
    },
  }
}
